import type { 
  UserLatchEvent, 
  UserLatchUser, 
  QueuedEvent, 
  RecordingPayload,
  SessionStartPayload,
  SessionChunkPayload,
  SessionEndPayload,
} from './types';

// NOTE: HTTP endpoints use .convex.site, NOT .convex.cloud
// .convex.cloud is for queries/mutations, .convex.site is for HTTP actions
const DEFAULT_ENDPOINT = 'https://veracious-firefly-862.convex.site';

/** Check if the given user should be tracked (not in blocklist). Returns true on network error (fail-open). */
export async function shouldTrack(
  apiKey: string,
  email: string,
  endpoint?: string
): Promise<boolean> {
  const base = endpoint || DEFAULT_ENDPOINT;
  try {
    const res = await fetch(`${base}/shouldTrack`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-UserLatch-Key': apiKey,
      },
      body: JSON.stringify({ email }),
    });
    const data = (await res.json()) as { track?: boolean };
    return data.track !== false;
  } catch {
    return true; // Fail open: track if we can't reach the server
  }
}

export class UserLatchAPI {
  private endpoint: string;
  private apiKey: string;
  private queue: QueuedEvent[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private flushInterval = 5000; // 5 seconds
  private userEmail: string | null = null;

  constructor(apiKey: string, endpoint?: string) {
    this.apiKey = apiKey;
    this.endpoint = endpoint || DEFAULT_ENDPOINT;
    this.setupFlushTimer();
    this.setupOnlineListener();
  }

  setUserEmail(email: string): void {
    this.userEmail = email;
  }

  private setupFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.flushInterval);
  }

  private setupOnlineListener(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.flush());
      window.addEventListener('beforeunload', () => this.flush());
    }
  }

  async identify(user: UserLatchUser): Promise<void> {
    this.queue.push({
      type: 'identify',
      payload: user,
      timestamp: Date.now(),
    });
    await this.flush();
  }

  track(event: UserLatchEvent): void {
    this.queue.push({
      type: 'track',
      payload: {
        ...event,
        userEmail: this.userEmail,
      },
      timestamp: Date.now(),
    });
  }

  sendRecording(sessionId: string, events: unknown[]): void {
    if (!this.userEmail) return;
    
    const payload: RecordingPayload = {
      sessionId,
      events,
      userEmail: this.userEmail,
    };

    this.queue.push({
      type: 'recording',
      payload,
      timestamp: Date.now(),
    });

    // Flush immediately for recordings to avoid data loss
    this.flush();
  }

  // Session API methods - use the new /session endpoint

  async startSession(payload: SessionStartPayload): Promise<{ sessionDocId?: string }> {
    try {
      const response = await fetch(`${this.endpoint}/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-UserLatch-Key': this.apiKey,
        },
        body: JSON.stringify({
          action: 'start',
          payload,
        }),
      });

      if (response.ok) {
        return await response.json();
      }
      console.error('[UserLatch] Failed to start session:', response.statusText);
      return {};
    } catch (error) {
      console.error('[UserLatch] Network error starting session:', error);
      return {};
    }
  }

  async sendSessionChunk(payload: SessionChunkPayload): Promise<boolean> {
    try {
      const response = await fetch(`${this.endpoint}/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-UserLatch-Key': this.apiKey,
        },
        body: JSON.stringify({
          action: 'chunk',
          payload,
        }),
      });

      if (!response.ok) {
        console.error('[UserLatch] Failed to send session chunk:', response.statusText);
        return false;
      }
      return true;
    } catch (error) {
      console.error('[UserLatch] Network error sending session chunk:', error);
      return false;
    }
  }

  async endSession(payload: SessionEndPayload): Promise<boolean> {
    try {
      // Use fetch with keepalive for reliability on page unload
      const response = await fetch(`${this.endpoint}/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-UserLatch-Key': this.apiKey,
        },
        body: JSON.stringify({
          action: 'end',
          payload,
        }),
        // keepalive helps the request complete even if the page is unloading
        keepalive: true,
      });

      return response.ok;
    } catch (error) {
      console.error('[UserLatch] Network error ending session:', error);
      return false;
    }
  }

  async flush(): Promise<void> {
    if (this.queue.length === 0) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;

    const events = [...this.queue];
    this.queue = [];

    try {
      const response = await fetch(`${this.endpoint}/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-UserLatch-Key': this.apiKey,
        },
        body: JSON.stringify({ events }),
      });

      if (!response.ok) {
        // Put events back in queue on failure
        this.queue = [...events, ...this.queue];
        console.error('[UserLatch] Failed to send events:', response.statusText);
      }
    } catch (error) {
      // Put events back in queue on network error
      this.queue = [...events, ...this.queue];
      console.error('[UserLatch] Network error:', error);
    }
  }

  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
  }
}
